/**
 * Dragon's Breath — Fire/Ember Particle System
 * Canvas-based particle effect for the product page hero section.
 *
 * Targets a <canvas id="db-particles"> element. Renders glowing ember
 * particles that rise from the bottom of the viewport with a slight
 * sine-wave wobble, fading out as they climb. Includes occasional
 * "heat-haze" particles for atmospheric depth.
 *
 * Performance: object-pooled particles, minimal per-frame allocations,
 * visibility-aware animation loop, debounced resize handling.
 */
;(function () {
  'use strict';

  // ---------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------

  /** Palette of warm fire colours (hex strings). */
  var COLORS = [
    '#FF4500', // orange-red
    '#FF6B00', // deep orange
    '#CC2200', // crimson
    '#FFB700', // amber
    '#FF4500',
    '#FF6B00',
    '#FFB700'
  ];

  /** Probability (0-1) that a newly spawned particle is white-hot. */
  var WHITE_HOT_CHANCE = 0.06;

  /** Probability (0-1) that a spawn produces a heat-haze particle. */
  var HEAT_HAZE_CHANCE = 0.08;

  /** Breakpoint (px) below which we consider the device "mobile". */
  var MOBILE_BREAKPOINT = 768;

  /** Target particle counts. */
  var DESKTOP_COUNT = 80;
  var MOBILE_COUNT = 40;

  /** Debounce delay for window resize (ms). */
  var RESIZE_DEBOUNCE_MS = 200;

  // ---------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------

  /**
   * Return a random float in [min, max).
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  function rand(min, max) {
    return min + Math.random() * (max - min);
  }

  /**
   * Pick a random entry from an array.
   * @param {Array} arr
   * @returns {*}
   */
  function pick(arr) {
    return arr[(Math.random() * arr.length) | 0];
  }

  /**
   * Convert a hex colour string to an {r, g, b} object.
   * Expects a 7-character string like "#FF4500".
   * @param {string} hex
   * @returns {{r: number, g: number, b: number}}
   */
  function hexToRgb(hex) {
    var v = parseInt(hex.slice(1), 16);
    return {
      r: (v >> 16) & 0xff,
      g: (v >> 8) & 0xff,
      b: v & 0xff
    };
  }

  // Pre-compute RGB values for palette colours to avoid per-frame parsing.
  var COLORS_RGB = COLORS.map(hexToRgb);
  var WHITE_HOT_RGB = { r: 255, g: 255, b: 240 };

  // ---------------------------------------------------------------
  // Particle Pool
  // ---------------------------------------------------------------

  /**
   * A single particle. All properties are mutable and reused via the
   * pool — no allocations happen during the animation loop.
   * @constructor
   */
  function Particle() {
    // Position
    this.x = 0;
    this.y = 0;

    // Movement
    this.speedY = 0;       // upward velocity (px / frame)
    this.wobbleAmp = 0;    // horizontal sine amplitude (px)
    this.wobbleFreq = 0;   // horizontal sine frequency
    this.wobbleOffset = 0; // phase offset so particles don't sync

    // Appearance
    this.size = 0;
    this.r = 0;
    this.g = 0;
    this.b = 0;
    this.glowSize = 0;     // shadowBlur radius

    // Lifecycle
    this.opacity = 0;
    this.life = 0;         // 1 → 0 over lifetime
    this.decay = 0;        // amount subtracted from life per frame
    this.active = false;

    // Heat-haze flag (larger, fainter, slower)
    this.haze = false;
  }

  /**
   * Pre-allocated particle pool.
   * @constructor
   * @param {number} capacity – maximum particles to allocate
   */
  function Pool(capacity) {
    this.particles = new Array(capacity);
    this.capacity = capacity;

    for (var i = 0; i < capacity; i++) {
      this.particles[i] = new Particle();
    }
  }

  /**
   * Find the first inactive particle and return it, or null if none
   * are available.
   * @returns {Particle|null}
   */
  Pool.prototype.acquire = function () {
    for (var i = 0; i < this.capacity; i++) {
      if (!this.particles[i].active) {
        return this.particles[i];
      }
    }
    return null;
  };

  // ---------------------------------------------------------------
  // Spawn Logic
  // ---------------------------------------------------------------

  /**
   * Initialise (or re-initialise) a particle with randomised
   * properties. The particle is activated and ready for the next
   * render frame.
   *
   * @param {Particle} p   – particle to initialise
   * @param {number} cw    – canvas width
   * @param {number} ch    – canvas height
   */
  function spawnParticle(p, cw, ch) {
    var isHaze = Math.random() < HEAT_HAZE_CHANCE;
    p.haze = isHaze;

    // --- Position --------------------------------------------------
    // ~85 % spawn from the bottom 20 % of the canvas; remainder from
    // the left/right edges for variety.
    var fromSide = Math.random() < 0.15;

    if (fromSide) {
      // Spawn along left or right edge, in the lower 60 %
      p.x = Math.random() < 0.5 ? rand(0, cw * 0.05) : rand(cw * 0.95, cw);
      p.y = rand(ch * 0.4, ch);
    } else {
      p.x = rand(0, cw);
      p.y = rand(ch * 0.8, ch);
    }

    // --- Movement --------------------------------------------------
    p.speedY = isHaze ? rand(0.2, 0.6) : rand(0.6, 2.0);
    p.wobbleAmp = rand(0.4, 2.5);
    p.wobbleFreq = rand(0.01, 0.04);
    p.wobbleOffset = rand(0, Math.PI * 2);

    // --- Appearance ------------------------------------------------
    if (isHaze) {
      p.size = rand(6, 14);
      p.opacity = rand(0.03, 0.07);
      p.glowSize = rand(10, 24);
    } else {
      p.size = rand(1, 4);
      p.opacity = rand(0.6, 1);
      p.glowSize = rand(4, 12);
    }

    // Colour
    var isWhiteHot = !isHaze && Math.random() < WHITE_HOT_CHANCE;
    var rgb = isWhiteHot ? WHITE_HOT_RGB : pick(COLORS_RGB);
    p.r = rgb.r;
    p.g = rgb.g;
    p.b = rgb.b;

    // --- Lifecycle --------------------------------------------------
    p.life = 1;
    p.decay = isHaze ? rand(0.002, 0.005) : rand(0.004, 0.012);
    p.active = true;
  }

  // ---------------------------------------------------------------
  // Main System
  // ---------------------------------------------------------------

  /**
   * Boot the particle system once the DOM is ready.
   */
  function init() {
    var canvas = document.getElementById('db-particles');
    if (!canvas) {
      return; // Nothing to do — element not present on this page.
    }

    var ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    // Current canvas dimensions (updated on resize).
    var width = 0;
    var height = 0;

    // Determine target particle count based on viewport width.
    function targetCount() {
      return window.innerWidth < MOBILE_BREAKPOINT
        ? MOBILE_COUNT
        : DESKTOP_COUNT;
    }

    // Allocate pool at the larger count so we never need to
    // re-allocate if the window is resized to desktop width.
    var pool = new Pool(DESKTOP_COUNT);

    // Track animation frame id so we can cancel if needed.
    var rafId = 0;
    var paused = false;

    // Elapsed frame counter for wobble phase.
    var frame = 0;

    // ------ Canvas Sizing ----------------------------------------

    /**
     * Synchronise the canvas element size with its CSS layout size
     * (respects devicePixelRatio for sharp rendering).
     */
    function resize() {
      var dpr = window.devicePixelRatio || 1;
      var rect = canvas.getBoundingClientRect();

      width = rect.width;
      height = rect.height;

      canvas.width = width * dpr;
      canvas.height = height * dpr;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    // Debounced resize handler.
    var resizeTimer = 0;

    function onResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(resize, RESIZE_DEBOUNCE_MS);
    }

    window.addEventListener('resize', onResize);
    resize(); // initial sizing

    // ------ Visibility Handling -----------------------------------

    function onVisibilityChange() {
      if (document.hidden) {
        paused = true;
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = 0;
        }
      } else {
        paused = false;
        if (!rafId) {
          rafId = requestAnimationFrame(tick);
        }
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange);

    // ------ Animation Loop ----------------------------------------

    /**
     * Core render tick. Spawns, updates, and draws all active
     * particles, then schedules the next frame.
     */
    function tick() {
      rafId = requestAnimationFrame(tick);
      frame++;

      // Clear canvas (transparent background).
      ctx.clearRect(0, 0, width, height);

      var count = targetCount();
      var active = 0;

      // --- Update & Draw active particles -------------------------
      for (var i = 0; i < pool.capacity; i++) {
        var p = pool.particles[i];
        if (!p.active) {
          continue;
        }

        // Update life
        p.life -= p.decay;
        if (p.life <= 0) {
          p.active = false;
          continue;
        }

        // Movement
        p.y -= p.speedY;
        p.x += Math.sin((frame + p.wobbleOffset) * p.wobbleFreq) * p.wobbleAmp;

        // Deactivate if out of bounds
        if (p.y < -p.size || p.x < -p.size || p.x > width + p.size) {
          p.active = false;
          continue;
        }

        // Compute current opacity (fades with life)
        var alpha = p.opacity * p.life;
        if (alpha < 0.005) {
          p.active = false;
          continue;
        }

        // --- Draw ------------------------------------------------
        var color = 'rgba(' + p.r + ',' + p.g + ',' + p.b + ',' + alpha + ')';

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);

        // Glow
        ctx.shadowColor = color;
        ctx.shadowBlur = p.glowSize * p.life;

        ctx.fillStyle = color;
        ctx.fill();

        active++;
      }

      // Reset shadow state once after the drawing pass to avoid
      // leaking into any future compositing.
      ctx.shadowBlur = 0;

      // --- Spawn new particles to reach target count ---------------
      // We spawn a handful per frame rather than all at once to
      // create a continuous stream effect.
      var deficit = count - active;
      var spawnPerFrame = Math.min(deficit, 3);

      for (var s = 0; s < spawnPerFrame; s++) {
        var np = pool.acquire();
        if (np) {
          spawnParticle(np, width, height);
        }
      }
    }

    // Kick off the loop.
    rafId = requestAnimationFrame(tick);
  }

  // ---------------------------------------------------------------
  // Bootstrap
  // ---------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
